import { isCompanyAdmin, useAuth } from '@commander/auth';
import { useFeatureFlags } from '@commander/feature-flags';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@commander/ui';
import { appRegistry } from '@commander/utils';
import {
  RiBuilding2Line as AccountIcon,
  RiKey2Line as ApiAccessIcon,
  RiMoneyDollarCircleLine as CommissionIcon,
  RiTruckLine as HaulerCentralIcon,
  RiLogoutCircleRLine as LogoutIcon,
  RiMapPinLine as MapPinIcon,
  RiPlayListAddLine,
  RiUserLine as UserIcon,
} from '@remixicon/react';

/** HaulerCentral is its own app, so it opens in a new tab rather than inside this one. */
function openHaulerCentral() {
  const app = appRegistry.getAppById('haulercentral');
  let url = '';

  if (app) {
    url = appRegistry.getAppUrl('haulercentral');
  } else {
    // Fallback if not in registry
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isStaging = hostname.includes('stg') || hostname.includes('staging');

    if (isLocalhost) {
      url = 'http://localhost:3005';
    } else if (isStaging) {
      url = 'https://stg.haulercentral.com/';
    } else {
      url = 'https://haulercentral.com/';
    }
  }

  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * The avatar menu every header shares: who is signed in, the pages they can reach (by role and
 * feature flag) and Log out. `go` moves to a page, so each header chooses how: the app header
 * routes inside the shell, the Action Dash header opens its single links tab.
 */
export function ProfileMenu({ go }: { go: (path: string) => void }) {
  const { user, logout } = useAuth();
  const { flags } = useFeatureFlags();
  const isAdmin = isCompanyAdmin(user);
  const appAccess = user?.company_employee?.app_access ?? [];
  const hasProspectingOnly = appAccess.includes('prospecting') && !appAccess.includes('crm');

  const openCommissions = () => {
    go(hasProspectingOnly ? '/commissions/access-denied' : '/commissions');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          title="Your profile"
          aria-label="Your profile"
          className="relative h-9 w-9 rounded-full"
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.avatar} alt={user?.name} />
            <AvatarFallback>
              {user?.name
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user?.name || 'User'}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email || 'user@commander.com'}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => go('/profile-settings')}>
          <UserIcon className="mr-2 h-4 w-4" />
          Profile Settings
        </DropdownMenuItem>
        {flags.haulercentral && (
          <DropdownMenuItem onClick={openHaulerCentral}>
            <HaulerCentralIcon className="mr-2 h-4 w-4" />
            Haulercentral
          </DropdownMenuItem>
        )}
        {flags.haulercentral && (
          <DropdownMenuItem onClick={() => go('/profile/rfqs')}>
            <RiPlayListAddLine className="mr-2 h-4 w-4" />
            Quote Requests
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <DropdownMenuItem onClick={() => go('/account')}>
            <AccountIcon className="mr-2 h-4 w-4" />
            Account
          </DropdownMenuItem>
        )}
        {isAdmin && flags.partner_api_docs && (
          <DropdownMenuItem onClick={() => go('/account/api')}>
            <ApiAccessIcon className="mr-2 h-4 w-4" />
            API Access
          </DropdownMenuItem>
        )}
        {isAdmin && (
          <DropdownMenuItem onClick={() => go('/territories')}>
            <MapPinIcon className="h-4 w-4 mr-2" />
            Territories
          </DropdownMenuItem>
        )}
        {flags.commission_tracking && (
          <DropdownMenuItem onClick={openCommissions}>
            <CommissionIcon className="h-4 w-4 mr-2" />
            Commissions
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => logout()} className="text-red-600">
          <LogoutIcon className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
